import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Fab,
  Badge,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { INotification, INotificationItem, NotificationApi } from '@/api/notification-api';
import { useUser } from '@/contexts/UserContext';
import { asyncScheduler } from 'rxjs';
import Messages from './Messages';
import { useCookies } from "react-cookie";

interface Notification {
  id: string
  message: string
  createdAt: string
  read: boolean
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [cookies, setCookie] = useCookies(["lastSeenId"]);
  const { user } = useUser();
  const [notifications, setNotifications] = useState<INotification>({ data: [], seen: [], read_noti: [] });
  const [showNumber, setShowNumber] = useState(0);
  const [markReadIds, setMarkReadIds] = useState<string[]>([])
  const [markUnReadIds, setMarkUnReadIds] = useState<string[]>([])

  const updateNotificationCount = (data: INotificationItem[] | undefined) => {
    // Count unread notifications based on read property
    const unreadCount = (data || notifications.data).filter(item => !item.read).length;
    setShowNumber(unreadCount);
  }

  const removeDuplicate = (arr: any[]) => {
    const set = new Set();
    return arr.reduce((finalList, obj) => {
      const key = `${obj.oldId}`;
      if (!set.has(key)) {
        finalList.push(obj)
      }
      set.add(key);
      return finalList;
    }, []);
  }

  useEffect(() => {
    // Initial fetch
    fetchNotifications();
    
    // Set up polling
    const subscription = asyncScheduler.schedule(function() {
      fetchNotifications();
      this.schedule(0, 60 * 1000); // Poll every minute
    }, 0);
    
    return () => subscription.unsubscribe();
  }, []);

  const fetchNotifications = async () => {
    try {
      const notificationData = await NotificationApi.getNotifications2(user);
      
      // Process the data
      let data = [...(notificationData.data || [])];
      data = removeDuplicate(data);
      data.sort((a, b) => Number(b.id) - Number(a.id));
      
      // Update state with read status from API
      setNotifications({
        data: data,
        seen: notificationData.seen || [],
        read_noti: notificationData.read_noti || []
      });
      
      // Update notification count
      updateNotificationCount(data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleOpen = () => {
    // find max value in data
    var maxId = Math.max(...notifications.data.map((item) => Number(item.id)), 0);
    setCookie("lastSeenId", maxId.toString(), { path: "/" });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // Refresh notifications after closing to update read status
    if (markReadIds && markReadIds.length > 0) {
      NotificationApi.markNotificationsAsRead(user, markReadIds, 1)
        .then(() => {
          // Refresh notifications after marking as read
          fetchNotifications()
        })
        .catch((err) => {
          console.error("Error marking notifications as read:", err)
        })
    } else if(markUnReadIds && markUnReadIds.length > 0) {
      NotificationApi.markNotificationsAsRead(user, markUnReadIds, 0)
        .then(() => {
          // Refresh notifications after marking as read
          fetchNotifications()
        })
        .catch((err) => {
          console.error("Error marking notifications as read:", err)
        })
    }else {
      // Refresh notifications after closing to update read status
      fetchNotifications()
    }
  };

  return (
    <>
      <Fab 
        size="small" 
        color="secondary"
        onClick={handleOpen}
        sx={{
          position: 'absolute',
          right: -20,
          top: -20,
          p: 0
        }}
      >
        <NotificationsIcon />
        {showNumber > 0 && (
          <Badge
            badgeContent={showNumber}
            color="info"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              '& .MuiBadge-badge': {
                top: -15,
              },
            }}
          />
        )}
      </Fab>

      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDialogContent-root': {
            padding: 0
          },
          '& .MuiListItem-root': {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
        PaperProps={{
          sx: {
            width: '800px',
            maxWidth: '90vw',
            position: 'absolute',
            top: '60px',
            right: '20px',
          }
        }}
      >
        <DialogContent>
          <Messages
            data={notifications}
            onClose={handleClose}
            markReadIds={markReadIds}
            setMarkReadIds={setMarkReadIds}
            setMarkUnReadIds={setMarkUnReadIds}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}